import React, { bindActionCreators } from "react";
import PropTypes from "prop-types";
import { IntlProvider } from "react-intl";

import { connect } from "react-redux";
import { withRouter } from "react-router-dom";
import Immutable, { List, Map } from "immutable";
import { formValueSelector } from "redux-form";

import { stripCommas } from "lib/utils/numberFunctions";

import { resetGivingForm } from "data/giving-form";

import { selectCartAllocations } from "data/giving-form/allocation-selection";

import { toggleModal } from "data/giving-form/routes";

import { setCampaign } from "data/parent-campaign";

import { getEntity, getEntityId, getLocale } from "data/entity";

import GivingModal from "./GivingModal";

import givingFormLocales from "shared/locales/givingForm";
import { stripOpenGivingFormFlagFromHash } from "shared/custom-hooks/use-listen-giving-form-router";

class Donation extends React.PureComponent {
  state = {
    isModalOpen: false,
    selectedLocale: null,
  };

  constructor(props) {
    super(props);

    if (typeof props.campaign !== "object" && !Map.isMap(props.campaign)) {
      throw new Error(
        `${
          "A GivingForm requires an immutable campaign to be passed as a prop." +
          " You passed an instance of type "
        }${typeof props.campaign}`,
      );
    }
  }

  launchModal = () => {
    const {
      autoLaunchOn,
      campaign,
      setCampaign,
      selectCartAllocations,
      toggleModal,
      location,
      history,
    } = this.props;

    if (typeof autoLaunchOn === "function" && autoLaunchOn()) {
      setCampaign(campaign);

      // PersistentCartService
      if (window.PersistentCartService) {
        selectCartAllocations();
      }

      toggleModal(true);

      this.setState({
        isModalOpen: true,
      });

      const nextHash = stripOpenGivingFormFlagFromHash(location.hash);
      if (nextHash !== location.hash) {
        history.replace({
          pathname: location.pathname,
          search: location.search,
          hash: nextHash,
          state: location.state,
        });
      }
    }
  };

  handleHashChange = () => {
    const { listenToHashChange } = this.props;

    if (listenToHashChange) {
      this.launchModal();
    }
  };

  componentWillUpdate(nextProps, nextState) {
    const { launchLocation, entityName, entityId } = this.props;

    if (!this.state.isModalOpen && nextState.isModalOpen) {
      window &&
        window.SnapAnalytics &&
        window.SnapAnalytics.track({
          hitType: "event",
          eventCategory: "giving form",
          eventLabel: launchLocation,
          eventAction: "open",
        });
    }
  }

  componentDidMount() {
    this.launchModal();

    window.addEventListener("hashchange", this.handleHashChange);
  }

  componentWillUnmount() {
    window.removeEventListener("hashchange", this.handleHashChange);

    window.SnapAnalytics && window.SnapAnalytics.clear();
  }

  handleModalOpen = () => {
    const { setCampaign, campaign, entity, toggleModal, selectCartAllocations } =
      this.props;

    setCampaign(campaign);
    // PersistentCartService
    if (window.PersistentCartService) {
      selectCartAllocations();
    }
    toggleModal(true);

    this.setState({
      isModalOpen: true,
    });
  };

  handleClick = (e) => {
    const { campaign, trackGiveButtonEvent } = this.props;

    e.preventDefault();
    trackGiveButtonEvent();

    if (campaign.get("is_touchnet") || campaign.get("is_cashnet")) {
      const url = campaign.get("url");
      window.location = `${url}#shareWizard`;
    } else {
      this.handleModalOpen();
    }
  };

  handleLanguageChange = (event) => {
    const lang = event.target.value;

    this.setState({
      selectedLocale: lang,
    });
  };

  handleClose = (e) => {
    const { toggleModal, onClose } = this.props;

    if (typeof window.EventAnalyticsService != "undefined") {
      window.EventAnalyticsService.addEvent({
        name: "GIVING FORM - CLOSE MODAL",
        giving_form_version: "React",
      });
    }

    toggleModal(false);
    this.setState({
      isModalOpen: false,
    });

    if (typeof onClose === "function") {
      onClose();
    }
  };

  renderHtml() {
    const { markup } = this.props;

    return { __html: markup };
  }

  render() {
    const { dontPreselectCampaign, shellCampaign, markup, children, entity } = this.props;
    const { isModalOpen, selectedLocale } = this.state;
    const currencyCode = entity.get("currency_code");
    const { locale: defaultLocale, available_locales: availableLocales } = this.props;
    const selected = selectedLocale ? selectedLocale : defaultLocale;

    if (markup) {
      return (
        <IntlProvider locale={selected} messages={givingFormLocales[selected]}>
          <div className="inline-b">
            <div onClick={this.handleClick} dangerouslySetInnerHTML={this.renderHtml()} />
            <GivingModal
              currencyCode={currencyCode}
              availableLocales={availableLocales}
              selectedLocale={selected}
              handleLanguageChange={this.handleLanguageChange}
              isModalOpen={isModalOpen}
              onClose={this.handleClose}
              dontPreselectCampaign={dontPreselectCampaign}
              shellCampaign={shellCampaign}
            />
          </div>
        </IntlProvider>
      );
    }
    return (
      <div className="inline-b">
        <div onClick={this.handleClick}>{children}</div>
        <GivingModal
          currencyCode={currencyCode}
          availableLocales={availableLocales}
          selectedLocale={selected}
          handleLanguageChange={this.handleLanguageChange}
          isModalOpen={isModalOpen}
          onClose={this.handleClose}
          dontPreselectCampaign={dontPreselectCampaign}
          shellCampaign={shellCampaign}
        />
      </div>
    );
  }
}

Donation.propTypes = {
  markup: PropTypes.string,
  entity: PropTypes.object.isRequired,
  entityId: PropTypes.string.isRequired,
  campaign: PropTypes.object.isRequired,
  launchLocation: PropTypes.string.isRequired,
  autoLaunchOn: PropTypes.func,
  dontPreselectCampaign: PropTypes.bool,
  shellCampaign: PropTypes.bool,
  listenToHashChange: PropTypes.bool,
  trackGiveButtonEvent: PropTypes.func,
};

const selector = formValueSelector("donation");

const mapStateToProps = (state) => {
  const { default_locale: locale, available_locales } = state.entity.toJSON();

  return {
    entity: getEntity(state),
    entityId: getEntityId(state),
    available_locales,
    locale,
  };
};
const mapDispatchToProps = (dispatch) => {
  return {
    setCampaign: (campaign) => {
      dispatch(setCampaign(campaign));
    },
    toggleModal: (bool) => {
      dispatch(toggleModal(bool));
    },
    selectCartAllocations: () => {
      dispatch(selectCartAllocations());
    },
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(Donation));
